import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Users, Plus, Trash2, ArrowRight } from 'lucide-react';
import { fetchStaffPartnerships, fetchAvailablePartnerships, deletePartnershipThunk } from '../../../../features/partnerships/partnershipsThunks';
import { setFilters, setPage, setRowsPerPage, setSearch } from '../../../../features/partnerships/partnershipsSlice';
import { fetchAdminOptions } from '../../../../features/admins/adminsThunks';
import { DataTable } from '../../../../components/ui/DataTable';
import { PageHeader } from '../../../../components/ui/PageHeader';
import { FilterBar } from '../../../../components/ui/FilterBar';
import { SearchInput } from '../../../../components/ui/SearchInput';
import { Button } from '../../../../components/ui/Button';
import { PendingButton } from '../../../../components/ui/PendingButton';
import { CreatePartnershipModal } from './CreatePartnershipModal';

// Debounce hook
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}

export const Partnerships = () => {
  const dispatch = useDispatch();

  const { partnerships, totalCount, loading, error, filters } = useSelector(state => state.partnerships);
  const { user } = useSelector(state => state.auth);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Local state for instant UI update on input, debounced for API
  const [localSearch, setLocalSearch] = useState(filters.search || '');
  const debouncedSearch = useDebounce(localSearch, 500);

  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      dispatch(setSearch(debouncedSearch));
    }
  }, [debouncedSearch, filters.search, dispatch]);

  useEffect(() => {
    dispatch(fetchStaffPartnerships());
    dispatch(fetchAvailablePartnerships());
    // The operators, for the two ends of a new alliance.
    dispatch(fetchAdminOptions());
  }, [dispatch, filters.page, filters.rowsPerPage, filters.search, filters.orderBy, filters.order]);

  const handleDelete = (id) => {
    if (window.confirm("¿Estás seguro que quieres eliminar esta alianza?")) {
      dispatch(deletePartnershipThunk({ partnershipId: id, isStaff: true }));
    }
  };

  const columns = [
    {
      accessor: 'source',
      header: 'Origen',
      cell: (item) => (
        <div className="flex flex-col">
          <span className="font-bold text-ninja-dark dark:text-white">
            {item.source_admin_details?.name || `Admin #${item.source_admin}`}
          </span>
          <span className="text-xs text-text-muted mt-0.5">
            {item.source_admin_details?.email || ''}
          </span>
        </div>
      )
    },
    {
      accessor: 'target',
      header: 'Destino',
      cell: (item) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 font-bold text-ninja-dark dark:text-white">
            <ArrowRight size={14} className="text-ninja-green" />
            {item.target_admin_details?.name || `Admin #${item.target_admin}`}
          </div>
          <span className="text-xs text-text-muted mt-0.5">
            {item.target_admin_details?.email || ''}
          </span>
        </div>
      )
    },
    {
      accessor: 'capacity',
      header: 'Capacidad del destino',
      cell: (item) => (
        <span className="font-medium">{item.target_admin_details?.upselling_capacity ?? 0}</span>
      )
    },
    {
      accessor: 'cost',
      header: 'Tarifa acordada',
      cell: (item) => (
        <span className="font-semibold text-ninja-dark dark:text-white">
          ${Number(item.base_cost ?? 0).toLocaleString('es-CL')}
        </span>
      )
    },
    {
      accessor: 'actions',
      header: 'Acciones',
      cell: (item) => (
        <div className="flex items-center justify-end">
          <PendingButton
            variant="subtle"
            size="sm"
            iconOnly
            icon={Trash2}
            pendingOn={deletePartnershipThunk}
            pendingKey={item.id}
            title="Eliminar Alianza"
            aria-label="Eliminar Alianza"
            className="hover:!text-danger hover:!bg-red-50 dark:hover:!bg-red-900/20"
            onClick={() => handleDelete(item.id)}
          />
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-300">

      <PageHeader
        icon={Users}
        iconClassName="bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400"
        title="Alianzas de Despacho"
        description="Todas las alianzas de la plataforma, de quién deriva a quién."
        actions={
          <Button variant="primary" icon={Plus} onClick={() => setIsModalOpen(true)}>
            Crear Alianza
          </Button>
        }
      />

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger p-4 rounded-xl border border-red-200 dark:border-red-900 flex items-center gap-3">
          <span className="font-semibold">Error:</span> {error}
        </div>
      )}

      <FilterBar variant="bare">
        <SearchInput
          variant="bare"
          value={localSearch}
          onChange={setLocalSearch}
          placeholder="Buscar alianzas..."
        />
      </FilterBar>

      <DataTable
        columns={columns}
        data={partnerships}
        isLoading={loading}
        totalItems={totalCount}
        currentPage={filters.page}
        itemsPerPage={filters.rowsPerPage}
        onPageChange={(page) => dispatch(setPage(page))}
        onRowsPerPageChange={(rows) => dispatch(setRowsPerPage(rows))}
        itemLabel="alianzas"
      />

      <CreatePartnershipModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};
